import React from 'react';
import { NodeType, GridNode, HoverElement, InteractionState } from '../../types';
import { getNodeIcon } from '../../utils/nodeIcons';

interface DragState {
  isDragging: boolean;
  nodeId: string | null;
  dragType: 'position' | 'value' | 'rating' | 'pan';
}

interface NodeProps {
  node: GridNode;
  isSelected: boolean;
  isBusSelected?: boolean;
  isDragging: boolean;
  onMouseDown: (event: React.MouseEvent<SVGGElement>, nodeId: string) => void;
  onContextMenu: (event: React.MouseEvent<SVGGElement>, nodeId: string) => void;
  interactionState: InteractionState;
  dragState: DragState;
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
  batteryStates?: Map<string, { soc: number; power: number }>;
}

const getNodeColor = (type: NodeType): string => {
  switch (type) {
    case 'generator': return '#4CAF50';
    case 'load': return '#F44336';
    case 'bus': return '#2196F3';
    case 'battery': return '#9C27B0';
  }
};

const getCapacityPercentage = (node: GridNode, batteryStates?: Map<string, { soc: number; power: number }>): number => {
  if (node.type === 'bus') return 0;
  
  if (node.type === 'generator') {
    const min = node.minPowerMW || 0;
    const max = node.maxPowerMW || node.value;
    if (max <= min) return 0;
    return Math.max(0, Math.min(1, (node.value - min) / (max - min)));
  }
  
  if (node.type === 'load') {
    // For loads, assume maximum capacity is the absolute value
    // and current usage is also absolute value
    const maxCapacity = Math.abs(node.maxPowerMW || node.value);
    const currentUsage = Math.abs(node.value);
    if (maxCapacity === 0) return 0;
    return Math.max(0, Math.min(1, currentUsage / maxCapacity));
  }
  
  if (node.type === 'battery') {
    // For batteries, show state of charge percentage
    const batteryState = batteryStates?.get(node.id);
    if (batteryState) {
      const maxCapacity = node.maxEnergyMWh || 500; // Default capacity if not set
      return Math.max(0, Math.min(1, batteryState.soc / maxCapacity));
    }
    // Fallback to initial energy if no current state available
    const maxCapacity = node.maxEnergyMWh || 500;
    const initialCapacity = node.initialEnergyMWh || maxCapacity / 2;
    return Math.max(0, Math.min(1, initialCapacity / maxCapacity));
  }
  
  return 0;
};

const createArcPath = (centerX: number, centerY: number, radius: number, percentage: number): string => {
  if (percentage === 0) return '';
  
  const startAngle = -Math.PI / 2; // Start at top
  const endAngle = startAngle + (2 * Math.PI * percentage);
  
  const x1 = centerX + radius * Math.cos(startAngle);
  const y1 = centerY + radius * Math.sin(startAngle);
  const x2 = centerX + radius * Math.cos(endAngle);
  const y2 = centerY + radius * Math.sin(endAngle);
  
  const largeArcFlag = percentage > 0.5 ? 1 : 0;
  
  if (percentage >= 1) {
    // Full circle
    return `M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 1 1 ${centerX + radius} ${centerY} A ${radius} ${radius} 0 1 1 ${centerX - radius} ${centerY}`;
  }
  
  return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
};

export function Node({
  node,
  isSelected,
  isBusSelected,
  isDragging,
  onMouseDown,
  onContextMenu,
  interactionState,
  dragState,
  hoverElement,
  onElementHover,
  batteryStates,
}: NodeProps) {
  const isValueDragging = dragState.isDragging && dragState.nodeId === node.id && dragState.dragType === 'value';
  const capacityPercentage = getCapacityPercentage(node, batteryStates);
  const shouldShowCapacityIndicator = (node.type === 'generator' || node.type === 'battery') && capacityPercentage > 0;
  const isHighlighted = hoverElement?.type === 'Node' && hoverElement?.id === node.id;
  
  return (
    <g 
      onMouseDown={(e) => onMouseDown(e, node.id)}
      onContextMenu={(e) => onContextMenu(e, node.id)}
      onMouseEnter={() => onElementHover({ type: 'Node', id: node.id })}
      onMouseLeave={() => onElementHover(null)}
      style={{ 
        cursor: dragState.nodeId === node.id ? 'grabbing' : 
                interactionState.mode === 'adding-edge' ? 'pointer' :
                'grab',
        userSelect: 'none'
      }}
    >
      {/* Capacity indicator background circle */}
      {shouldShowCapacityIndicator && (
        <circle
          cx={node.x}
          cy={node.y}
          r="32"
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="6"
          opacity={0.3}
        />
      )}
      
      {/* Capacity indicator arc */}
      {shouldShowCapacityIndicator && (
        <path
          d={createArcPath(node.x, node.y, 32, capacityPercentage)}
          fill={node.type === 'generator' ? '#4CAF50' : node.type === 'battery' ? '#9C27B0' : '#F44336'}
          opacity={0.7}
          stroke="none"
        />
      )}
      
      {/* LMP-curve selection ring */}
      {isBusSelected && (
        <circle
          cx={node.x}
          cy={node.y}
          r="30"
          fill="none"
          stroke="#1565C0"
          strokeWidth="3"
          strokeDasharray="4,3"
        />
      )}
      <circle
        cx={node.x}
        cy={node.y}
        r="25"
        fill={getNodeColor(node.type)}
        stroke={isHighlighted ? '#FFD700' : isSelected ? '#FF9800' : '#333'}
        strokeWidth={isHighlighted ? 4 : isSelected ? 4 : 2}
        opacity={isValueDragging ? 0.8 : 1}
      />
      {/* Value adjustment indicator */}
      {isValueDragging && (
        <circle
          cx={node.x}
          cy={node.y}
          r="30"
          fill="none"
          stroke="#FF9800"
          strokeWidth="2"
          strokeDasharray="5,5"
        />
      )}
      <text
        x={node.x}
        y={node.y + 5}
        textAnchor="middle"
        fontSize="14"
        fill="white"
        fontWeight="bold"
        pointerEvents="none"
      >
        {getNodeIcon(node)}
      </text>
      {/* Node label */}
      <text
        x={node.x}
        y={node.y + 45}
        textAnchor="middle"
        fontSize="12"
        fill="#333"
        pointerEvents="none"
      >
        {node.id}
      </text>
      {/* Value label */}
      <text
        x={node.x}
        y={node.y + 60}
        textAnchor="middle"
        fontSize="11"
        fill="#666"
        pointerEvents="none"
      >
        {node.value.toFixed(2)}MW
      </text>
      {/* LMP label */}
      {node.lmp !== undefined && (
        <text
          x={node.x}
          y={node.y + 75}
          textAnchor="middle"
          fontSize="10"
          fill="#FF6600"
          fontWeight="bold"
          pointerEvents="none"
        >
          ${node.lmp.toFixed(2)}/MWh
        </text>
      )}
    </g>
  );
}
